import { createApp } from 'vue';
import App from './App.vue';
import router from "./router";
import AOS from 'aos';
import 'aos/dist/aos.css';

// Suppress harmless ResizeObserver error (browser limitation, not a real error)
const resizeObserverErr = window.onerror;
window.onerror = (message, ...args) => {
    if (typeof message === 'string' && message.includes('ResizeObserver')) {
        return true; // Suppress
    }
    return resizeObserverErr ? resizeObserverErr(message, ...args) : false;
};
import i18n from './i18n';
import { applyFlatpickrLocale } from './common/flatpickr-config';
import { temaIniziale, applicaTema } from './common/tema';
import { dimensioneIniziale as dimensioneMenuIniziale, applica as applicaDimensioneMenu } from './common/menu-laterale';

// Prima di ogni altra cosa, e prima che Vue disegni: altrimenti a ogni ricarica si vede
// un lampo bianco prima che la pagina passi allo scuro.
applicaTema(temaIniziale());

// Stessa ragione, stesso momento: il menu ridotto deve gia' esserlo al primo fotogramma,
// altrimenti si vede aperto e poi chiudersi a ogni caricamento.
applicaDimensioneMenu(dimensioneMenuIniziale());

// Nell'app, una classe su <html> che dice «siamo nell'app». Serve agli stili che devono valere
// anche dove il guscio mobile non c'e' — la pagina d'accesso e quella del secondo fattore non
// passano da Layout — per esempio i messaggi a comparsa, che vanno spostati sotto la testata e
// sotto la tacca. Nel sito la condizione e' falsa e la classe non esiste.
if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
  document.documentElement.classList.add("piattaforma-mobile");

  // IL TASTO (E IL GESTO) INDIETRO DI ANDROID.
  //
  // Capacitor da solo non lo gestisce: senza il plugin App ogni pressione portava fuori
  // dall'app, anche dal dettaglio di una prenotazione. Con l'ascoltatore le regole sono tre.
  //
  // - Con una finestra aperta non succede niente. Tornare indietro smonterebbe la pagina senza
  //   chiudere la finestra, e il velo delle modali Bootstrap (dettaglio sul calendario, nuova
  //   prenotazione) resterebbe sul corpo della pagina a bloccare ogni tocco.
  // - Con un pannello che si chiude col tasto indietro (data-indietro-chiude: il foglio dei modelli
  //   nella casella di risposta) si chiude lui, e basta, anche dentro una finestra: e' quello che ci
  //   si aspetta da un foglio salito dal basso, e prima si lasciava la pagina con la risposta a meta'.
  //   Il pannello riceve l'evento "indietro" e si chiude da se'; l'ultimo aperto, se ce n'e' piu' d'uno.
  // - Altrimenti si torna alla pagina precedente dell'app.
  // - L'app va in background (come fa Android da solo dalla versione 12) quando indietro non c'e'
  //   niente: prima voce della cronologia, oppure quando la voce prima e' l'accesso o il secondo
  //   fattore. Da li' le guardie del router rimbalzerebbero di nuovo qui, in un giro senza uscita:
  //   provato con il vue-router del progetto e le sue guardie vere.
  //
  // Un import dinamico dentro questo ramo: nel sito la condizione e' la costante falsa, il ramo
  // sparisce e il plugin non entra nel pacchetto.
  import("@capacitor/app").then(({ App }) => {
    App.addListener("backButton", ({ canGoBack }) => {
      const corpo = document.body.classList;
      if (corpo.contains("swal2-shown") && !corpo.contains("swal2-toast-shown")) {
        return;
      }
      // Prima delle finestre: il pannello puo' stare dentro una (la casella nella scheda della prenotazione,
      // sul calendario), e chiuderlo non smonta niente.
      // Solo quelli a schermo: la casella della scheda della prenotazione resta montata anche nascosta (v-show).
      const pannelli = [...document.querySelectorAll("[data-indietro-chiude]")]
        .filter((el) => el.getClientRects().length > 0);
      if (pannelli.length) {
        pannelli[pannelli.length - 1].dispatchEvent(new CustomEvent("indietro"));
        return;
      }
      if (corpo.contains("modal-open") || document.querySelector(".modal.show")) {
        return;
      }
      const prima = window.history.state?.back;
      // Solo da autenticati: da /forgot-password, aperta da /login senza accesso, indietro deve
      // tornare a /login invece di mandare l'app in background.
      const dopoAccesso = keycloakService.isAuthenticated()
        && /^\/(login|secondo-fattore)(?:[/?#]|$)/.test(prima || "");
      if (!canGoBack || !prima || dopoAccesso) {
        App.minimizeApp();
      } else {
        window.history.back();
      }
    });
  });

  // LE NOTIFICHE PUSH, PREDISPOSTE PRIMA DI OGNI ALTRA COSA.
  //
  // Qui e non dopo l'accesso: Capacitor azzera gli ascoltatori dei plugin a ogni caricamento
  // della pagina, e il tocco su una notifica che ha aperto l'app a freddo resta in attesa finche'
  // qualcuno non si mette in ascolto. Prima ci si mette, prima si sa dove portare la persona.
  // predisponi() non chiama il server: la registrazione del telefono parte solo a sessione
  // completa, da avvia() (qui sotto dopo apriSessione, e nel guscio mobile).
  import("@/services/notifichePush")
    .then((m) => m.default.predisponi())
    .catch((e) => console.warn("Notifiche push non predisposte:", e?.message || e));
}
import store from "./state/store";
import keycloakService from './services/keycloak';
import { vediCome } from './services/vediCome';
import sicurezzaService from './services/sicurezzaService';
import eventi from './services/eventi';
import messaggiNonLetti from './services/messaggiNonLetti';

import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import BootstrapVueNext from 'bootstrap-vue-next';
// Senza, le BModal si aprono sotto il proprio velo: il velo le copre e non si clicca niente.
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import vClickOutside from "click-outside-vue3";
import VueApexCharts from "vue3-apexcharts";
import { vMaska } from "maska"

import VueFeather from 'vue-feather';

import '@/assets/scss/config/modern/app.scss';
import '@/assets/scss/mermaid.min.css';
// Lo stesso Bootstrap di "import { Modal } from 'bootstrap'" nelle pagine, e non il bundle: sono due copie, ognuna coi suoi
// ascoltatori sul documento, e un clic su un data-bs-toggle apriva con una e richiudeva con l'altra.
import 'bootstrap'

AOS.init({
    easing: 'ease-out-back',
    duration: 1000
});

// Il calendario deve nascere nella lingua salvata, non sempre in italiano.
applyFlatpickrLocale(i18n.global.locale.value);

/**
 * Quanto si aspetta la risposta sul secondo fattore prima di montare comunque.
 *
 * Otto secondi: abbastanza perche' una risposta lenta arrivi in tempo e la schermata del
 * codice sia la prima cosa che si vede, poco abbastanza perche' un backend bloccato non
 * diventi una pagina bianca. Non e' un timeout della richiesta — quella resta in volo e,
 * quando risponde, fa il suo lavoro.
 */
const ATTESA_MASSIMA_FATTORE = 8000;

const attendi = (ms) => new Promise((esegui) => setTimeout(esegui, ms));

/**
 * IL LOGO D'AVVIO DELL'APP SE NE VA QUANDO LA PRIMA PAGINA E' PRONTA, E MAI PRIMA DI 1,5 SECONDI.
 *
 * Il logo lo tiene a schermo il nativo (@capacitor/splash-screen): qui si dice soltanto quando
 * toglierlo. Nel sito la condizione e' la costante falsa, il ramo sparisce e il plugin non entra
 * nel pacchetto.
 *
 * PRONTA vuol dire finita la prima navigazione del router, guardie comprese (/login, /secondo-fattore
 * o la pagina vera), non solo Vue montato: le pagine si caricano a richiesta, e subito dopo mount()
 * il contenuto e' ancora vuoto. Togliendo il logo li' si vedrebbe il navy nudo, cioe' proprio il
 * vuoto che il logo deve coprire.
 *
 * IL MINIMO di 1,5 secondi si conta dall'inizio della pagina (performance.now()), non da qui: il
 * logo e' a schermo da prima ancora che la WebView carichi, quindi a conti fatti resta qualcosa di
 * piu'. Serve a non far lampeggiare il logo quando la sessione c'e' e il telefono e' veloce.
 *
 * IL TETTO non sta qui ma in capacitor.config.json (launchShowDuration 5000, launchAutoHide true):
 * se questo codice non arriva mai - un errore prima del montaggio, una guardia che aspetta un server
 * muto - il logo se ne va da solo. Cinque secondi e non di piu': finche' c'e' il logo l'app non
 * disegna, e un tasto indietro premuto in un'attesa piu' lunga rischia «L'app non risponde». Per lo
 * stesso motivo nessuna attesa legata ai fotogrammi (requestAnimationFrame): non ne arriverebbero.
 *
 * NIENTE DISSOLVENZA (launchFadeOutDuration 0), e non per gusto: con una durata maggiore di zero il
 * plugin registra setOnExitAnimationListener, il percorso del crash noto su Android 12 e su alcuni
 * Oppo/OnePlus con Android 13 quando l'app va in background col logo a schermo e poi scatta hide()
 * o il tetto (capacitor-plugins#1856). Con zero l'uscita e' quella di sistema.
 *
 * Si chiama dopo ciascuno dei due montaggi: se il catch rimonta dopo un errore del primo, la seconda
 * hide() trova il logo gia' tolto e non fa niente. Se hide() fallisce ci pensa il tetto.
 */
const LOGO_MINIMO_MS = 1500;
function togliLogoQuandoPronta() {
    if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
        router.isReady()
            .catch(() => {})
            .then(() => attendi(Math.max(0, LOGO_MINIMO_MS - performance.now())))
            .then(() => import("@capacitor/splash-screen"))
            .then(({ SplashScreen }) => SplashScreen.hide())
            .catch((errore) => console.warn("Logo d'avvio non tolto dal JS, ci pensa il tetto nativo:", errore));
    }
}

// Initialize Keycloak (check if already authenticated via SSO)
keycloakService.init().then(async (authenticated) => {
    // Senza una sessione non si sta guardando nessuno: chi entra dopo, in questa scheda, deve essere se stesso.
    if (!authenticated) vediCome.pulisci();
    if (authenticated) {
        // Store user profile in Vuex store
        const userProfile = keycloakService.getUserProfile();
        store.commit('auth/SET_CURRENT_USER', userProfile);

        // Il secondo fattore, PRIMA di aprire qualunque canale.
        //
        // Una sessione ripristinata da disco ha il token ma non e' detto che abbia dato il
        // codice: riaprendo il browser il sid resta lo stesso, quindi di norma risulta gia'
        // verificata e qui non succede niente. Quando invece serve, si aspetta questa risposta
        // prima di far partire il canale degli eventi e il conteggio dei messaggi: sono le due
        // cose che chiamano il server da sole, e senza secondo fattore prenderebbero un 403
        // ciascuna mentre la persona sta ancora leggendo la schermata del codice.
        const apertura = sicurezzaService.apriSessione().then((serveFattore) => {
            if (!serveFattore) {
                // Il canale con cui il server ci avverte che e' successo qualcosa: uno solo per
                // tutta l'applicazione, aperto qui una volta sola. All'uscita si chiude da se',
                // perche' il logout porta via la pagina. Le due avvia() sono idempotenti,
                // quindi se la risposta arriva in ritardo — dopo che l'applicazione e' gia'
                // montata — i canali partono comunque e senza raddoppiarsi.
                eventi.avvia();
                messaggiNonLetti.avvia();
                // Nell'app anche la registrazione per le notifiche push, idempotente come le
                // due qui sopra: copre la risposta che arriva dopo gli otto secondi, quando il
                // guscio mobile si e' gia' montato e la sua avvia() e' uscita a vuoto.
                if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
                    import("@/services/notifichePush")
                        .then((m) => m.default.avvia())
                        .catch(() => { /* senza push l'app funziona lo stesso */ });
                }
            }
        });

        // SI ASPETTA, MA NON PER SEMPRE.
        //
        // Il montaggio dell'applicazione sta a valle di questo punto, e apiClient non ha alcun
        // timeout (axios, per difetto, aspetta all'infinito). Un backend che accetta la
        // connessione e poi non risponde — un riavvio preso a meta', un proxy che tiene aperto
        // — lascerebbe la pagina BIANCA e muta a ogni avvio a freddo, senza nemmeno un errore
        // da guardare: il caso peggiore diventerebbe il piu' silenzioso.
        //
        // Montare comunque non apre niente: la difesa e' il filtro lato server, che risponde
        // 403 a tutto, e l'intercettore di api.js porta la persona sulla schermata del codice.
        // Al peggio si vede l'applicazione per un istante prima di essere rimbalzati la'.
        await Promise.race([apertura, attendi(ATTESA_MASSIMA_FATTORE)]);
    } else if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
        // L'app parte senza una sessione valida: token scaduto o revocato, rinnovo rifiutato. In
        // questi percorsi keycloak.js non chiama logout(), quindi il gancio che toglie il telefono
        // dalle notifiche non parte: senza questa riga il telefono continuerebbe a mostrare gli
        // ospiti di chi c'era prima, anche a chi entra dopo.
        //
        // MA SOLO SE LA SESSIONE E' FINITA DAVVERO. init() risponde false anche quando il rinnovo
        // non si e' potuto chiedere (niente rete, Keycloak in 5xx) e il token d'accesso e' gia'
        // scaduto, cosa normale pochi minuti dopo aver chiuso l'app: li' i token restano salvati,
        // perche' la sessione di trenta giorni e' ancora buona. Dimenticare il telefono in quel caso
        // svuotava la tendina (anche offline) e, con Keycloak in 5xx ma FCM raggiungibile, cancellava
        // il gettone: niente push fino alla prossima apertura riuscita. Vedi haSessioneSalvata().
        if (!keycloakService.haSessioneSalvata()) {
            import("@/services/notifichePush")
                .then((m) => m.default.dimentica())
                .catch(() => { /* e' pulizia: non deve fermare l'avvio */ });
        }
    }

    const app = createApp(App);

    // Make keycloak available globally
    app.config.globalProperties.$keycloak = keycloakService;

    // Global error handler - prevent runtime error overlay for handled errors
    app.config.errorHandler = (err, instance, info) => {
        // Network errors are already handled by API interceptor with toast
        if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
            console.warn('Network error (handled by toast):', err.message);
            return;
        }
        // Log other errors
        console.error('Vue error:', err, info);
    };

    app.use(store)
        .use(router)
        .use(VueApexCharts)
        .use(BootstrapVueNext)
        .component(VueFeather.type, VueFeather)
        .directive("maska", vMaska)
        .use(i18n)
        .use(VueSweetalert2)
        .use(vClickOutside)
        .mount('#app');

    togliLogoQuandoPronta();

}).catch((error) => {
    console.error('Failed to initialize Keycloak:', error);

    // Come nel ramo senza sessione qui sopra: nessuno e' dentro, e il telefono non deve restare
    // registrato per le notifiche di chi c'era prima. Il controllo sull'accesso c'e' perche' questo
    // catch raccoglie anche un errore nato DOPO l'accesso (il montaggio qui sopra): li' la persona
    // e' dentro, e cancellarle il gettone la lascerebbe senza notifiche fino al prossimo avvio.
    if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
        if (!keycloakService.isAuthenticated()) {
            import("@/services/notifichePush")
                .then((m) => m.default.dimentica())
                .catch(() => { /* e' pulizia: non deve fermare l'avvio */ });
        }
    }

    // Still mount the app to show login page
    const app = createApp(App);
    app.config.globalProperties.$keycloak = keycloakService;

    app.use(store)
        .use(router)
        .use(VueApexCharts)
        .use(BootstrapVueNext)
        .component(VueFeather.type, VueFeather)
        .directive("maska", vMaska)
        .use(i18n)
        .use(VueSweetalert2)
        .use(vClickOutside)
        .mount('#app');

    togliLogoQuandoPronta();
});
